// Shared by drawing and input: where a line's end caps and trains sit on screen, what is under the pointer,
// and each line's colour as this player sees it (colour-blind mode swaps the palette, the game itself never changes).
import { S, ensureGeom, lineNext, trainSeg, pointAt, ease, dist } from '../sim/game.js';
import { UI } from './ui-state.js';

// line colours -> an Okabe-Ito based palette that stays apart for every common kind of colour blindness
const CB = ['#e69f00', '#56b4e9', '#009e73', '#f0e442', '#0072b2', '#d55e00', '#cc79a7', '#7f7f7f'];
const cbOf = new Map();
export function lineColor(L) {
  if (!UI.colourBlind) return L.color;
  if (!cbOf.has(L.color)) cbOf.set(L.color, CB[cbOf.size % CB.length]);
  return cbOf.get(L.color);
}

export const CAP_OUT = 26;   // how far past the terminus the buffer stop stands
// a line's two buffer stops (none on a loop): the end station, the cap's spot just beyond it and the way it faces
export function lineCaps(L) {
  ensureGeom();
  if (L.loop || !L.geo || !L.geo.length) return [];
  const caps = [];
  for (const [g, end] of [[L.geo[0], 0], [L.geo[L.geo.length - 1], L.st.length - 1]]) {
    const P = g.pts, a = end === 0 ? P[0] : P[P.length - 1], b = end === 0 ? P[Math.min(2, P.length - 1)] : P[Math.max(0, P.length - 3)];
    const l = dist(a, b) || 1, ux = (a.x - b.x) / l, uy = (a.y - b.y) / l;
    caps.push({ L, end, station: S(L.st[end]), from: a, x: a.x + ux * CAP_OUT, y: a.y + uy * CAP_OUT, ang: Math.atan2(uy, ux) });
  }
  return caps;
}
export function capAt(p, w) {
  let best = null, bd = 14;
  for (const L of p.lines) for (const c of lineCaps(L)) { const d = dist(c, w); if (d < bd) { bd = d; best = c; } }
  return best;
}

// where a train is drawn: at its station while it waits, eased along the track while it runs
export function trainPos(L, T) {
  if (L.st.length < 2) return null;
  if (T.to == null) {
    const nx = lineNext(L, T.idx, T.dir) ?? lineNext(L, T.idx, -T.dir);
    if (nx == null) return null;
    const a = S(L.st[T.idx]), sg = trainSeg(L, T.idx, nx), q = pointAt(sg.geo, sg.rev ? 1 : 0, sg.rev);
    return { x: a.x, y: a.y, ang: q.ang };
  }
  const k = ease(T.vt ?? T.t), sg = trainSeg(L, T.idx, T.to);
  return pointAt(sg.geo, sg.rev ? 1 - k : k, sg.rev);
}
// a running train under the pointer (trains waiting at a station are left to the station, which starts lines)
export function trainAt(p, w) {
  let best = null, bd = 16;
  for (const L of p.lines) L.trains.forEach((T, i) => {
    if (T.to == null) return;
    const q = trainPos(L, T); if (!q) return;
    const d = dist(q, w); if (d < bd) { bd = d; best = { L, i, T }; }
  });
  return best;
}
